/**
 * corridorMap.js
 *
 * Single source of truth for:
 *
 *   country
 *     -> currency
 *     -> dial code
 *     -> available payout channels
 *
 *   channel
 *     -> PSP
 *     -> rail/network
 *     -> recipient field schema
 *
 * MOBILE NUMBER CONVENTION
 * ------------------------
 * African mobile-money numbers are entered/stored in the UI WITHOUT
 * the national trunk prefix "0".
 *
 * Examples:
 *
 *   Ghana:
 *     UI: 241234567
 *     API: +233241234567
 *
 *   Kenya:
 *     UI: 712345678
 *     API: +254712345678
 *
 *   Uganda:
 *     UI: 701234567
 *     API: +256701234567
 *
 * Legacy national numbers such as 0712345678 are accepted and
 * normalized automatically by useCorridor.
 *
 * Providers:
 *   Bridge = EU / UK / US / Brazil
 *   Zuba   = African payout / off-ramp corridors
 *
 * Colombia intentionally omitted.
 */

// ============================================================================
// REUSABLE FIELD SPECS
// ============================================================================

const FIELD = {
  accountHolderName: {
    key: "account_holder_name",
    label: "Account Holder Name",
    type: "text",
  },

  recipientName: {
    key: "account_holder_name",
    label: "Recipient Name",
    type: "text",
  },

  iban: {
    key: "iban",
    label: "IBAN",
    type: "text",
    pattern: "^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$",
    placeholder: "DE89370400440532013000",
  },

  bicSwift: {
    key: "bic_swift",
    label: "BIC / SWIFT",
    type: "text",
    optional: true,
  },

  ukAccountNumber: {
    key: "account_number",
    label: "Account Number",
    type: "text",
    pattern: "^[0-9]{8}$",
    placeholder: "12345678",
  },

  sortCode: {
    key: "sort_code",
    label: "Sort Code",
    type: "text",
    pattern: "^[0-9]{2}-[0-9]{2}-[0-9]{2}$",
    placeholder: "20-00-00",
  },

  routingNumber: {
    key: "routing_number",
    label: "Routing Number (ABA)",
    type: "text",
    pattern: "^[0-9]{9}$",
    placeholder: "021000021",
  },

  usAccountNumber: {
    key: "account_number",
    label: "Account Number",
    type: "text",
  },

  accountType: (values) => ({
    key: "account_type",
    label: "Account Type",
    type: "enum",
    values,
  }),

  bankName: {
    key: "bank_name",
    label: "Bank Name",
    type: "text",
    optional: true,
  },

  bankAddress: {
    key: "bank_address",
    label: "Bank Address",
    type: "text",
  },

  pixKeyType: {
    key: "pix_key_type",
    label: "PIX Key Type",
    type: "enum",
    values: ["cpf", "cnpj", "phone", "email", "random"],
  },

  pixKey: {
    key: "pix_key",
    label: "PIX Key",
    type: "text",
  },

  mobileProvider: (values) => ({
    key: "mobileProvider",
    label: "Mobile Network",
    type: "enum",
    values,
  }),

  mobileNumber: (pattern) => ({
    key: "mobile_number",
    label: "Mobile Number",
    type: "text",
    pattern: pattern || undefined,
  }),

  bankCodeSelect: (source) => ({
    key: "bank_code",
    label: "Bank",
    type: "bank_select",
    source,
  }),

  africaAccountNumber: {
    key: "account_number",
    label: "Account Number",
    type: "text",
  },

  zaAccountType: {
    key: "account_type",
    label: "Account Type",
    type: "enum",
    values: ["cheque", "savings", "transmission"],
  },
};

function fields(...list) {
  return list;
}

// ============================================================================
// CHANNEL BUILDERS
// ============================================================================

function mobileMoneyChannel({ providers, pattern }) {
  return {
    label: "Mobile Money",
    psp: "zuba",
    channel: "mobile_money",
    settlement: "instant",
    providers,

    recipient_fields: fields(
      FIELD.mobileProvider(Object.keys(providers)),
      FIELD.mobileNumber(pattern),
      FIELD.recipientName
    ),
  };
}

function bankTransferAfricaChannel({ source, settlement }) {
  return {
    label: "Bank Transfer",
    psp: "zuba",
    channel: "bank_transfer",
    settlement: settlement || "1-3 business days",

    recipient_fields: fields(
      FIELD.bankCodeSelect(source),
      FIELD.africaAccountNumber,
      FIELD.accountHolderName
    ),
  };
}

// ============================================================================
// COUNTRY MAP
// ============================================================================

export const COUNTRIES = {
  // ==========================================================================
  // BRIDGE — SEPA
  // ==========================================================================

  ...Object.fromEntries(
    [
      ["AT", "Austria"],
      ["BE", "Belgium"],
      ["BG", "Bulgaria"],
      ["HR", "Croatia"],
      ["CY", "Cyprus"],
      ["CZ", "Czechia"],
      ["DK", "Denmark"],
      ["EE", "Estonia"],
      ["FI", "Finland"],
      ["FR", "France"],
      ["DE", "Germany"],
      ["GR", "Greece"],
      ["HU", "Hungary"],
      ["IS", "Iceland"],
      ["IE", "Ireland"],
      ["IT", "Italy"],
      ["LV", "Latvia"],
      ["LI", "Liechtenstein"],
      ["LT", "Lithuania"],
      ["LU", "Luxembourg"],
      ["MT", "Malta"],
      ["NL", "Netherlands"],
      ["NO", "Norway"],
      ["PL", "Poland"],
      ["PT", "Portugal"],
      ["RO", "Romania"],
      ["SK", "Slovakia"],
      ["SI", "Slovenia"],
      ["ES", "Spain"],
      ["SE", "Sweden"],
      ["CH", "Switzerland"],
      ["MD", "Moldova"],
      ["MC", "Monaco"],
      ["ME", "Montenegro"],
      ["MK", "North Macedonia"],
      ["SM", "San Marino"],
      ["RS", "Serbia"],
      ["VA", "Vatican City"],
    ].map(([code, name]) => [
      code,
      {
        name,
        dialCode: null,
        currency: "EUR",
        region: "SEPA",

        channels: {
          sepa: {
            label: "Bank Transfer (SEPA)",
            psp: "bridge",
            rail: "eur.sepa",
            channel: "sepa",

            recipient_fields: fields(
              FIELD.iban,
              FIELD.accountHolderName,
              FIELD.bicSwift
            ),
          },
        },
      },
    ])
  ),

  // ==========================================================================
  // BRIDGE — UNITED KINGDOM
  // ==========================================================================

  GB: {
    name: "United Kingdom",
    dialCode: "+44",
    currency: "GBP",
    region: "UK",

    channels: {
      fps: {
        label: "Bank Transfer (FPS)",
        psp: "bridge",
        rail: "gbp.fps",
        channel: "fps",

        recipient_fields: fields(
          FIELD.ukAccountNumber,
          FIELD.sortCode,
          FIELD.accountHolderName
        ),
      },
    },
  },

  // ==========================================================================
  // BRIDGE — UNITED STATES
  // ==========================================================================

  US: {
    name: "United States",
    dialCode: "+1",
    currency: "USD",
    region: "US",

    channels: {
      ach: {
        label: "Bank Transfer (ACH)",
        psp: "bridge",
        rail: "usd.ach",
        channel: "ach",
        settlement: "1-3 business days",

        recipient_fields: fields(
          FIELD.routingNumber,
          FIELD.usAccountNumber,
          FIELD.accountType(["checking", "savings"]),
          FIELD.accountHolderName,
          FIELD.bankName
        ),
      },

      wire: {
        label: "Wire Transfer",
        psp: "bridge",
        rail: "usd.wire",
        channel: "wire",
        settlement: "same day / next day",

        recipient_fields: fields(
          FIELD.routingNumber,
          FIELD.usAccountNumber,
          FIELD.accountHolderName,
          {
            key: "bank_name",
            label: "Bank Name",
            type: "text",
          },
          FIELD.bankAddress
        ),
      },
    },
  },

  // ==========================================================================
  // BRIDGE — BRAZIL
  // ==========================================================================

  BR: {
    name: "Brazil",
    dialCode: "+55",
    currency: "BRL",
    region: "Brazil",

    channels: {
      pix: {
        label: "PIX",
        psp: "bridge",
        rail: "brl.pix",
        channel: "pix",
        settlement: "instant (24/7)",

        recipient_fields: fields(
          FIELD.pixKeyType,
          FIELD.pixKey,
          {
            ...FIELD.recipientName,
            optional: true,
          }
        ),
      },
    },
  },

  // ==========================================================================
  // ZUBA — NIGERIA
  // ==========================================================================

  NG: {
    name: "Nigeria",
    dialCode: "+234",
    currency: "NGN",
    region: "Africa",

    channels: {
      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_NG",
        settlement: "instant (NIP/NIP+)",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — GHANA
  // ==========================================================================

  GH: {
    name: "Ghana",
    dialCode: "+233",
    currency: "GHS",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        // UI:
        //   241234567
        //
        // NOT:
        //   0241234567
        //
        // API:
        //   +233241234567

        pattern: "^[25][0-9]{8}$",

        providers: {
          MTN_GH: {
            label: "MTN Mobile Money (MoMo)",
            msisdnPrefix: ["024", "025", "053", "054", "055", "059"],
          },

          VODAFONE_GH: {
            label: "Telecel Cash (formerly Vodafone Cash)",
            msisdnPrefix: ["020", "050"],
          },

          AIRTELTIGO_GH: {
            label: "AirtelTigo Money",
            msisdnPrefix: ["026", "056", "027", "057"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_GH",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — KENYA
  // ==========================================================================

  KE: {
    name: "Kenya",
    dialCode: "+254",
    currency: "KES",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        // UI:
        //   712345678
        //
        // Also supports:
        //   112345678
        //
        // Legacy national:
        //   0712345678
        //
        // API:
        //   +254712345678

        pattern: "^[17][0-9]{8}$",

        providers: {
          MPESA_KE: {
            label: "M-Pesa (Safaricom)",
            msisdnPrefix: ["07", "01"],
          },

          AIRTEL_KE: {
            label: "Airtel Money Kenya",
            msisdnPrefix: ["073", "078"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_KE",
        settlement: "same day to 2 days",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — TANZANIA
  // ==========================================================================

  TZ: {
    name: "Tanzania",
    dialCode: "+255",
    currency: "TZS",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[67][0-9]{8}$",

        providers: {
          MPESA_TZ: {
            label: "M-Pesa Tanzania (Vodacom)",
            msisdnPrefix: ["074", "075", "076"],
          },

          TIGOPESA_TZ: {
            label: "Tigo Pesa (MIC Tanzania)",
            msisdnPrefix: ["071", "065", "067"],
          },

          AIRTEL_TZ: {
            label: "Airtel Money Tanzania",
            msisdnPrefix: ["068", "069", "078"],
          },

          HALOPESA_TZ: {
            label: "HaloPesa (Halotel)",
            msisdnPrefix: ["062", "061"],
          },

          AZAMPESA_TZ: {
            label: "AzamPesa (Azam Telecom)",
            msisdnPrefix: ["073"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_TZ",
        settlement: "1-2 business days",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — UGANDA
  // ==========================================================================

  UG: {
    name: "Uganda",
    dialCode: "+256",
    currency: "UGX",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[37][0-9]{8}$",

        providers: {
          MTN_UG: {
            label: "MTN Mobile Money Uganda",
            msisdnPrefix: ["076", "077", "078", "079", "039"],
          },

          AIRTEL_UG: {
            label: "Airtel Money Uganda",
            msisdnPrefix: ["070", "075"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_UG",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — SOUTH AFRICA
  // ==========================================================================

  ZA: {
    name: "South Africa",
    dialCode: "+27",
    currency: "ZAR",
    region: "Africa",

    channels: {
      bank_transfer: {
        label: "Bank Transfer (EFT / RTC)",
        psp: "zuba",
        channel: "bank_transfer",
        settlement: "instant (RTC) or same day (EFT)",

        recipient_fields: fields(
          FIELD.bankCodeSelect("zuba_banks_ZA"),
          FIELD.africaAccountNumber,
          FIELD.zaAccountType,
          FIELD.accountHolderName
        ),
      },
    },
  },

  // ==========================================================================
  // ZUBA — CAMEROON
  // ==========================================================================

  CM: {
    name: "Cameroon",
    dialCode: "+237",
    currency: "XAF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{9}$",

        providers: {
          MTN_CM: {
            label: "MTN Mobile Money Cameroon",
            msisdnPrefix: ["065", "066", "067", "068"],
          },

          ORANGE_CM: {
            label: "Orange Money Cameroon",
            msisdnPrefix: ["069"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_CM",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — RWANDA
  // ==========================================================================

  RW: {
    name: "Rwanda",
    dialCode: "+250",
    currency: "RWF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^7[0-9]{8}$",

        providers: {
          MTN_RW: {
            label: "MTN Mobile Money Rwanda",
            msisdnPrefix: ["078", "079"],
          },

          AIRTEL_RW: {
            label: "Airtel Money Rwanda",
            msisdnPrefix: ["072", "073"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_RW",
        settlement: "1-2 business days",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — ZAMBIA
  // ==========================================================================

  ZM: {
    name: "Zambia",
    dialCode: "+260",
    currency: "ZMW",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{9}$",

        providers: {
          MTN_ZM: {
            label: "MTN Mobile Money Zambia",
            msisdnPrefix: ["096", "076"],
          },

          AIRTEL_ZM: {
            label: "Airtel Money Zambia",
            msisdnPrefix: ["097", "077"],
          },

          ZAMTEL_ZM: {
            label: "Zamtel Kwacha",
            msisdnPrefix: ["095", "075"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_ZM",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — CÔTE D'IVOIRE
  // ==========================================================================

  CI: {
    name: "Côte d'Ivoire",
    dialCode: "+225",
    currency: "XOF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{10}$",

        providers: {
          MTN_CI: {
            label: "MTN Mobile Money Côte d'Ivoire",
            msisdnPrefix: ["05", "07"],
          },

          ORANGE_CI: {
            label: "Orange Money Côte d'Ivoire",
            msisdnPrefix: ["07"],
          },

          MOOV_CI: {
            label: "Moov Money Côte d'Ivoire",
            msisdnPrefix: ["01"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_CI",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — SENEGAL
  // ==========================================================================

  SN: {
    name: "Senegal",
    dialCode: "+221",
    currency: "XOF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^7[0-9]{8}$",

        providers: {
          ORANGE_SN: {
            label: "Orange Money Senegal",
            msisdnPrefix: ["07"],
          },

          FREE_SN: {
            label: "Free Money (Sonatel)",
            msisdnPrefix: ["076"],
          },

          EXPRESSO_SN: {
            label: "Expresso Money",
            msisdnPrefix: ["077"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_SN",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — DR CONGO
  // ==========================================================================

  CD: {
    name: "DR Congo",
    dialCode: "+243",
    currency: "CDF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{9}$",

        providers: {
          MPESA_CD: {
            label: "M-Pesa DRC (Vodacom)",
            msisdnPrefix: ["081", "082"],
          },

          AIRTEL_CD: {
            label: "Airtel Money DRC",
            msisdnPrefix: ["099", "097", "098"],
          },

          ORANGE_CD: {
            label: "Orange Money DRC",
            msisdnPrefix: ["084", "085", "086", "087"],
          },
        },
      }),
    },
  },

  // ==========================================================================
  // ZUBA — GABON
  // ==========================================================================

  GA: {
    name: "Gabon",
    dialCode: "+241",
    currency: "XAF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{8}$",

        providers: {
          AIRTEL_GA: {
            label: "Airtel Money Gabon",
            msisdnPrefix: ["074", "077"],
          },

          MOOV_GA: {
            label: "Moov Money Gabon",
            msisdnPrefix: ["066"],
          },
        },
      }),
    },
  },

  // ==========================================================================
  // ZUBA — MALAWI
  // ==========================================================================

  MW: {
    name: "Malawi",
    dialCode: "+265",
    currency: "MWK",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{9}$",

        providers: {
          AIRTEL_MW: {
            label: "Airtel Money Malawi",
            msisdnPrefix: ["099", "098", "097"],
          },

          TNM_MW: {
            label: "Mpamba (TNM)",
            msisdnPrefix: ["088", "084"],
          },
        },
      }),
    },
  },

  // ==========================================================================
  // ZUBA — BOTSWANA
  // ==========================================================================

  BW: {
    name: "Botswana",
    dialCode: "+267",
    currency: "BWP",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{8}$",

        providers: {
          ORANGE_BW: {
            label: "Orange Money Botswana",
            msisdnPrefix: ["076"],
          },

          MASCOM_BW: {
            label: "MyZaka (Mascom)",
            msisdnPrefix: ["073", "072"],
          },
        },
      }),

      bank_transfer: bankTransferAfricaChannel({
        source: "zuba_banks_BW",
        settlement: "1-2 business days",
      }),
    },
  },

  // ==========================================================================
  // ZUBA — REPUBLIC OF CONGO
  // ==========================================================================

  CG: {
    name: "Republic of Congo",
    dialCode: "+242",
    currency: "XAF",
    region: "Africa",

    channels: {
      mobile_money: mobileMoneyChannel({
        pattern: "^[0-9]{9}$",

        providers: {
          MTN_CG: {
            label: "MTN Mobile Money Congo",
            msisdnPrefix: ["06"],
          },

          AIRTEL_CG: {
            label: "Airtel Money Congo",
            msisdnPrefix: ["05"],
          },
        },
      }),
    },
  },
};

// ============================================================================
// ONRAMP
// ============================================================================

export const ONRAMP = {
  USD: {
    psp: "bridge",
    rail: "usd.ach",
  },

  GBP: {
    psp: "bridge",
    rail: "gbp.fps",
  },

  EUR: {
    psp: "bridge",
    rail: "eur.sepa",
  },
};

// ============================================================================
// COUNTRY HELPERS
// ============================================================================

export function getCountryList() {
  return Object.entries(COUNTRIES)
    .map(([code, country]) => ({
      code,
      name: country.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getChannelsForCountry(countryCode) {
  const country = COUNTRIES[countryCode?.toUpperCase()];

  if (!country) {
    return {};
  }

  return country.channels;
}

export function getChannel(countryCode, channelKey) {
  return getChannelsForCountry(countryCode)[channelKey] || null;
}

// ============================================================================
// KYC ROUTING
// ============================================================================

export const BRIDGE_KYC_CURRENCIES = [
  "USD",
  "GBP",
  "EUR",
];

export function isBridgeCountry(countryCode) {
  const country = COUNTRIES[countryCode?.toUpperCase()];

  if (!country) {
    return false;
  }

  return BRIDGE_KYC_CURRENCIES.includes(country.currency);
}

export function getKycRoute(_countryCode) {
  return "/kyc";
}

// ============================================================================
// PIX VALIDATION
// ============================================================================

export function validatePixKey(pixKeyType, value) {
  const patterns = {
    cpf: /^[0-9]{11}$/,

    cnpj: /^[0-9]{14}$/,

    phone: /^\+55[0-9]{10,11}$/,

    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

    random: /^[0-9a-fA-F-]{36}$/,
  };

  const re = patterns[pixKeyType];

  return re ? re.test(String(value || "").trim()) : false;
}

// ============================================================================
// GENERIC FIELD VALIDATION
// ============================================================================

export function validateField(fieldSpec, value) {
  if (fieldSpec.optional && !value) {
    return true;
  }

  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return false;
  }

  if (fieldSpec.pattern) {
    return new RegExp(fieldSpec.pattern).test(
      String(value).trim()
    );
  }

  return true;
}

// ============================================================================
// USER-FACING VALIDATION ERRORS
// ============================================================================

export function getFieldErrorMessage(field) {
  if (field.type === "enum") {
    return `Select a valid ${field.label.toLowerCase()}`;
  }

  switch (field.key) {
    case "iban":
      return "Enter a valid IBAN";

    case "bic_swift":
      return "Enter a valid BIC/SWIFT code";

    case "sort_code":
      return "Enter a valid sort code (format: 12-34-56)";

    case "routing_number":
      return "Enter a valid 9-digit routing number";

    case "mobile_number":
      return "Enter a valid mobile number";

    case "pix_key":
      return "Enter a valid PIX key for the selected type";

    case "pix_key_type":
      return "Select a valid PIX key type";

    case "bank_code":
      return "Select a valid bank";

    case "account_number":
      return "Enter a valid account number";

    case "account_holder_name":
      return "Enter the account holder name";

    default:
      return `Enter a valid ${field.label.toLowerCase()}`;
  }
}